import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  CenteredContainer,
  PageWrapper,
  PaginationControls,
} from "~/components";
import { useTableSearchParams } from "~/hooks";
import { useGetFirmwares } from "~/hooks/useFirmwares";
import { ROUTES } from "~/router";
import { useFirmwareStore } from "~/stores";
import { Button, Card, Heading, Spinner } from "~/ui";

const PAGE_SIZE = 5;
export const Firmwares = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentPage, setCurrentPage } = useTableSearchParams();
  const { setFirmware } = useFirmwareStore();

  const { firmwaresData, isLoadingFirmwares, totalFirmwares } = useGetFirmwares(
    currentPage,
    PAGE_SIZE,
  );

  const handleShowFirmware = (fw: { id: number; name: string }) => {
    // Store firmware name in Zustand store
    setFirmware({ id: fw.id, name: fw.name });

    void navigate(ROUTES.firmwaresDetail.replace(":id", fw.id.toString()), {
      state: { firmwareName: fw.name },
    });
  };

  const firmwares = firmwaresData?.data;

  return (
    <PageWrapper>
      <Heading>{t("firmware.firmwares", "Firmwares")}</Heading>
      <div className="mt-4 flex h-full flex-col justify-between gap-8 overflow-hidden">
        {isLoadingFirmwares && (
          <CenteredContainer>
            <Spinner />
          </CenteredContainer>
        )}
        {!isLoadingFirmwares && !totalFirmwares && (
          <CenteredContainer>
            <p className="text-lg text-neutral-80">
              {t("firmware.no_data", "No firmwares available")}
            </p>
          </CenteredContainer>
        )}
        {!isLoadingFirmwares && totalFirmwares && (
          <div className="flex flex-col gap-16">
            <div className="flex flex-col gap-4">
              {firmwares?.map((fw) => (
                <Card key={fw.id} className="flex w-full flex-row">
                  <div className="flex grow flex-col gap-2">
                    <h3 className="text-lg font-semibold text-primary-50 dark:text-primary-80">
                      {fw.name}
                    </h3>
                    <p className="font-medium text-neutral-60 dark:text-tertiary">
                      {fw.description}
                    </p>
                  </div>
                  <Button
                    className="h-fit self-center"
                    text="Show"
                    variant="outline"
                    color="primary"
                    size="sm"
                    aria-label={`Show firmware ${fw.name}`}
                    onClick={() => handleShowFirmware(fw)}
                  />
                </Card>
              ))}
            </div>
            <PaginationControls
              currentPage={currentPage}
              numPages={Math.ceil((totalFirmwares ?? 0) / PAGE_SIZE)}
              setPage={setCurrentPage}
            />
          </div>
        )}
      </div>
    </PageWrapper>
  );
};
